import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { CourseSessionOption, TopicGroupOption, useTopicGroupsOptions, useTopicsOptions } from "api/hooks";
import { ScheduleFormBase, BaseScheduleForm } from "./BaseScheduleForm";
import { BaseTrainersScheduleForm, ScheduleFormTrainers } from "../BaseTrainersScheduleForm";
import { BasePlaceScheduleForm, ScheduleFormPlace } from "../BasePlaceScheduleForm";
import { PaziaButton, PaziaSelect } from "components/atoms";
import { minutesToTime } from "utils";
import { CourseApi, CourseScheduleDetailsApi, TopicTrainierApi } from "api";

import { components } from "react-select";
import { TopicGroupApi } from "api/topicgroups/topicgroupsApi";

interface ScheduleFormProps {
  course: CourseApi;
  /** Resolve `false` when nothing was saved (e.g. the user went back after a conflict warning): the form keeps its values. */
  onSubmit: (data: ScheduleTopicGroupFormSchema) => Promise<boolean | void> | void;
  item?: CourseScheduleDetailsApi;
  sessionsOptions: CourseSessionOption[];
  formStyle: React.CSSProperties;
  formClassName?: string;
}

export const ScheduleTopicGroupForm = ({
  course,
  onSubmit,
  sessionsOptions,
  item,
  formStyle,
  formClassName,
}: ScheduleFormProps): JSX.Element => {
  const [editTrainers, setEditTrainers] = React.useState(!item?.schedule);
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      ...ScheduleFormBase.resolveDefaultValues(item?.schedule),
      ...ScheduleFormPlace.resolveDefaultValues(item?.schedule),
      ...ScheduleFormTrainers.resolveDefaultValues(item?.trainers),
      [SCHEDULE_TOPIC_GROUP_FORM_NAME.TOPIC_GROUP]: item?.schedule?.topicGroupId?.toString() ?? "",
    },
  });
  const { handleSubmit, reset, setValue } = methods;
  const {
    group: { id },
  } = course;
  const { loading: loadingTopicGroups, options: topicGroupsOptions } = useTopicGroupsOptions(`courseGroups in (${id})`);
  const { options: topicsOptions } = useTopicsOptions(false, `courseGroups in (${id})`);
  const onSubmitHandler = async (data: ScheduleTopicGroupFormSchema) => {
    if ((await onSubmit(data)) === false) return;
    reset();
  };

  const onTopicGroupChange = (option: TopicGroupOption) => {
    const topicGroupOpt = topicGroupsOptions.find((x) => x.value == option.value);
    const topicIds = topicGroupOpt?.topicGroup?.topics.map((x) => x.id) ?? [];
    if (editTrainers) {
      const trainers = topicsOptions
        .filter((x) => x.topic && topicIds.includes(x.topic.id))
        .map((x) => x.topic!.trainers)
        .flat();

      const firstTrainersDistinctByTrainerId =
        trainers
          .reduce((acc: TopicTrainierApi[], current) => {
            const x = acc.find((item) => item.trainerId == current.trainerId);
            if (!x) {
              return acc.concat([current]);
            } else {
              return acc;
            }
          }, [])
          .map((x) => ({
            trainerId: x.trainerId.toString(),
            totalAmount: null,
          })) ?? [];
      setValue(ScheduleFormTrainers.fields.TRAINERS, firstTrainersDistinctByTrainerId);
    }
    const time = topicGroupOpt?.topicGroup?.time ?? 0;

    if (time === 0) return;
    setValue(ScheduleFormBase.fields.TIME, minutesToTime(time));
    setValue(ScheduleFormBase.fields.TO_TIME, 0);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmitHandler)} className="flex grow flex-col justify-between">
        <div className={` ${formClassName}`} style={formStyle}>
          <BaseScheduleForm sessionsOptions={sessionsOptions}>
            <PaziaSelect
              required
              label="Grupa tematów"
              isLoading={loadingTopicGroups}
              options={topicGroupsOptions}
              components={{ Option }}
              onChange={onTopicGroupChange}
              name={SCHEDULE_TOPIC_GROUP_FORM_NAME.TOPIC_GROUP}
              tooltip="Wyświetlane są jedynie tematy powiązane z wybraną grupą kursu."
              noOptionsMessage="Brak grup tematów powiązanych z grupą tego kursu — dodaj je w Tematy → Grupy tematów"
            />
          </BaseScheduleForm>
          <BasePlaceScheduleForm />
          <BaseTrainersScheduleForm />
        </div>
        <div className="mt-8 flex justify-end">
          <PaziaButton title={item ? "Zapisz zmiany" : "Dodaj zajęcia"} type="success" />
        </div>
      </form>
    </FormProvider>
  );
};

const Option = (props: any) => {
  const tg = props.data.topicGroup as TopicGroupApi;
  return (
    <div className="text-sm">
      <components.Option {...props} className="!py-1">
        {props.data.label}
        {tg && (
          <div className="flex flex-col">
            <div className="peer -mt-1 w-min text-nowrap text-xs text-gray-400 hover:underline">
              {/* TODO polskie formy  */}
              {minutesToTime(tg.time)} - {tg.topics.length} tematów
            </div>
            <div className="hidden peer-hover:block">
              {tg.topics.map((x) => (
                <span key={x.id} className="text-primary-500 block text-xs">
                  - {x.title}
                </span>
              ))}
            </div>
          </div>
        )}
      </components.Option>
    </div>
  );
};

const SCHEDULE_TOPIC_GROUP_FORM_NAME = {
  TOPIC_GROUP: "topicGroup",
} as const;

const topicSchema = yup
  .object({
    [SCHEDULE_TOPIC_GROUP_FORM_NAME.TOPIC_GROUP]: yup.string().required(),
  })
  .required();

const schema = topicSchema
  .concat(ScheduleFormBase.schema)
  .concat(ScheduleFormPlace.schema)
  .concat(ScheduleFormTrainers.schema);

export type ScheduleTopicGroupFormSchema = yup.InferType<typeof schema>;
